/**
 * <theme-toggle>: dark (the default) or light. A visitor who switches to light keeps it: the choice is stored in
 * localStorage (disclosed on the Privacy page). Switching back to dark clears it.
 */

// Dependencies
import { registerComponent } from 'almostnojs';
import { Enhanced } from './enhanced.js';

// Browser chrome colour per theme.
const CHROME = { dark: '#0a0e0f', light: '#f6f5f1' };

/**
 * The theme in effect.
 *
 * @returns {string} 'light' or 'dark'.
 */
const effective = () => (document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark');

/**
 * The theme switch.
 */
class ThemeToggle extends Enhanced {

    /**
     * Wire the button.
     *
     * @returns {void}
     */
    init() {

        // Button.
        this.button = this.querySelector('button');
        if (!this.button) return;

        // Label for what a click will do, and the browser chrome to match.
        this.label();

        // Switch.
        this.listen(this.button, 'click', () => this.choose(effective() === 'dark' ? 'light' : 'dark'));
    }

    /**
     * Apply a theme.
     *
     * @param {string} mode - 'light' or 'dark'.
     * @returns {void}
     */
    choose(mode) {

        // Remember light; dark is the default, so choosing it clears the choice.
        try {
            if (mode === 'light') localStorage.setItem('theme', 'light');
            else localStorage.removeItem('theme');
        } catch {
            // The choice lasts for this page only.
        }

        // Apply and announce.
        document.documentElement.setAttribute('data-theme', mode);
        this.label();
        document.dispatchEvent(new CustomEvent('themechange'));
    }

    /**
     * Describe the next theme on the button and match the browser chrome to the current one.
     *
     * @returns {void}
     */
    label() {

        // The theme a click switches to.
        const current = effective();
        this.button.setAttribute('aria-label', `Switch to ${current === 'dark' ? 'light' : 'dark'} theme`);

        // Address-bar colour on mobile browsers.
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', CHROME[current]);
    }
}

// Register
registerComponent('theme-toggle', ThemeToggle);
